// src/services/coordinatorService.js
//
// CRUD for the `coordinators` collection (mirrors studyService.js).
//
// Design notes that keep existing data intact:
//   - A patient's coordinator assignment is encoded inside the `status` string
//     (e.g. "Pending (Levi)"). We never rewrite those strings here.
//   - Leaderboard counts live in `coordinatorEnrollments` / `coordinatorWeekly`,
//     keyed by the coordinator's `name`. So the `name` field is the join key —
//     keep it stable for assignment/leaderboard continuity.
//   - "Delete" is a SOFT delete (active: false). We never call deleteDoc, so no
//     coordinator record is ever destroyed and existing assignments keep
//     rendering their coordinator's name.
//   - Rename appends to `nameHistory` rather than replacing, so an old status
//     string can always still be traced back to the coordinator.

import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  arrayUnion,
} from "firebase/firestore";
import { db } from "../firebase";
import { getCoordinatorsPath } from "./firestorePaths";

// ✅ Fetch all coordinators (active + inactive), sorted by `order` then name.
export const fetchCoordinators = async () => {
  const snapshot = await getDocs(collection(db, getCoordinatorsPath()));
  const list = snapshot.docs.map((d) => {
    const data = d.data();
    return {
      ...data,
      id: d.id, // force override LAST
    };
  });
  list.sort((a, b) => {
    const ao = a.order ?? 999;
    const bo = b.order ?? 999;
    if (ao !== bo) return ao - bo;
    return (a.name || "").localeCompare(b.name || "");
  });
  return list;
};

// ✅ Fetch only active coordinators (what dropdowns/leaderboard should use).
export const fetchActiveCoordinators = async () => {
  const all = await fetchCoordinators();
  return all.filter((c) => c.active !== false);
};

// ✅ Add a new coordinator. Auto-ID; `name` field is the join key.
export const createCoordinator = async (data) => {
  const name = (data.name || "").trim();
  await addDoc(collection(db, getCoordinatorsPath()), {
    name,
    email: data.email || "",
    includeInLeaderboard: data.includeInLeaderboard !== false,
    receivesUrgentEmail: data.receivesUrgentEmail !== false,
    receivesStaleBoxReminders: data.receivesStaleBoxReminders !== false,
    active: true,
    order: data.order ?? 999,
    nameHistory: [name],
  });
};

// ✅ Update fields on a coordinator. If `name` changes, append the new name to
// nameHistory (never lose the prior name).
export const updateCoordinator = async (id, updatedData, previousName) => {
  const ref = doc(db, getCoordinatorsPath(), id);
  const payload = { ...updatedData };
  const newName = (updatedData.name || "").trim();
  if (newName && previousName && newName !== previousName) {
    payload.nameHistory = arrayUnion(newName);
  }
  await updateDoc(ref, payload);
};

// ✅ Soft delete — deactivate, never remove. Existing patient assignments and
// leaderboard counts are untouched.
export const deactivateCoordinator = async (id) => {
  const ref = doc(db, getCoordinatorsPath(), id);
  await updateDoc(ref, { active: false });
};

// ✅ Reactivate a previously deactivated coordinator.
export const reactivateCoordinator = async (id) => {
  const ref = doc(db, getCoordinatorsPath(), id);
  await updateDoc(ref, { active: true });
};
